import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import {
  Client,
  MessageTemplate,
  WhatsAppCloudStatus,
  fetchTemplates,
  fetchWhatsAppCloudSpend,
  fetchWhatsAppCloudStatus,
  refreshTemplateStatuses,
  sendWhatsAppMessage,
} from '../services/api';
import MessageTemplateMetaStatus, { MessageTemplateSpendChip, MessageTemplateStatusLegend } from './MessageTemplateMetaStatus';
import { messageTemplateSendable } from '../utils/metaTemplateStatus';

interface Props {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  /** Fired after a successful send (caller can refresh its list / timeline). */
  onSent?: () => void;
}

/**
 * Shared "Send WhatsApp" compose dialog used by the client list row action
 * and the detail page. On the paired bridge the agent picks a template or
 * writes a blank message. On the Official API only Meta-approved templates
 * can be sent, unchanged. The backend performs placeholder substitution,
 * the budget check, and logs the activity + message body.
 */
export default function WhatsAppComposeDialog({ open, client, onClose, onSent }: Props) {
  const [step, setStep] = useState<'pick' | 'edit'>('pick');
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [cloudStatus, setCloudStatus] = useState<WhatsAppCloudStatus | null>(null);
  const [selected, setSelected] = useState<MessageTemplate | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Official API spend per template id. Null until the spend request finishes. */
  const [templateSpend, setTemplateSpend] = useState<Record<string, number> | null>(null);

  const usingCloud = cloudStatus?.provider === 'cloud';
  const budgetPaused = usingCloud && !!cloudStatus?.dailyBudget?.paused;

  // Reset + load templates every time the dialog opens for a different client.
  useEffect(() => {
    if (!open || !client) return;
    setStep('pick');
    setMessage('');
    setSelected(null);
    setError(null);
    setLoadingTemplates(true);
    setTemplateSpend(null);
    let cancelled = false;
    Promise.all([
      fetchTemplates('whatsapp').catch(() => [] as MessageTemplate[]),
      fetchWhatsAppCloudStatus().catch(() => null),
    ])
      .then(async ([tpls, status]) => {
        if (cancelled) return;
        setTemplates(tpls);
        setCloudStatus(status);
        if (status?.provider !== 'cloud') return;
        try {
          const spend = await fetchWhatsAppCloudSpend();
          if (cancelled) return;
          setTemplateSpend(Object.fromEntries(
            (spend.messageTemplates ?? []).map((t) => [String(t.templateId), t.cost]),
          ));
        } catch {
          if (!cancelled) setTemplateSpend(null);
        }
      })
      .finally(() => { if (!cancelled) setLoadingTemplates(false); });
    return () => { cancelled = true; };
  }, [open, client?.Id]);

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  const applyTemplate = (tpl: MessageTemplate) => {
    if (usingCloud && !messageTemplateSendable(tpl)) return;
    // Backend substitutes placeholders at send time, so we send the raw body.
    setSelected(tpl);
    setMessage(tpl.Body);
    setStep('edit');
  };

  const startBlank = () => {
    setSelected(null);
    setMessage('');
    setStep('edit');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const refreshed = await refreshTemplateStatuses();
      setTemplates(refreshed.filter((t) => t.Channel === 'whatsapp'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not refresh template status');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSend = async () => {
    if (!client || !message.trim()) return;
    if (usingCloud && !selected) return;
    setSending(true);
    setError(null);
    try {
      await sendWhatsAppMessage({
        clientId: client.Id,
        message: message.trim(),
        mobile: client.Mobile || undefined,
        templateId: usingCloud ? selected?.Id : undefined,
      });
      onSent?.();
      onClose();
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to send WhatsApp');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open && !!client}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
    >
      <DialogTitle fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WhatsAppIcon sx={{ color: '#25D366' }} />
        Send WhatsApp
      </DialogTitle>
      <DialogContent>
        {step === 'pick' ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
                {usingCloud
                  ? `Choose an approved template to send to ${client?.Name}`
                  : `Choose a template or start with a blank message to ${client?.Name}`}
              </Typography>
              {usingCloud && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { void handleRefresh(); }}
                  disabled={refreshing || loadingTemplates || !cloudStatus?.account.connected}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {refreshing ? 'Checking…' : 'Refresh status'}
                </Button>
              )}
            </Box>

            {usingCloud && !loadingTemplates && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-faint)', mb: 1 }}>
                  Sent through the Official API. Each message is estimated at S$0.10 and counts toward your daily budget.
                </Typography>
                <MessageTemplateStatusLegend />
              </Box>
            )}
            {budgetPaused && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                Today&apos;s Official API budget has been reached. Raise it under Settings → Integrations or try again tomorrow.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>
            )}

            {loadingTemplates ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Blank message option — bridge only; the Official API cannot send free text. */}
                {!usingCloud && (
                  <Box
                    onClick={startBlank}
                    sx={{
                      p: 1.5,
                      border: '1px dashed var(--color-border)',
                      borderRadius: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': { bgcolor: 'rgba(37, 211, 102, 0.05)', borderColor: '#25D366' },
                    }}
                  >
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '50%',
                      bgcolor: 'rgba(37, 211, 102, 0.1)', color: '#25D366',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <EditIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Blank Message</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                        Start from scratch
                      </Typography>
                    </Box>
                  </Box>
                )}

                {templates.map((tpl) => {
                  const sendable = !usingCloud || (!refreshing && messageTemplateSendable(tpl));
                  return (
                    <Box
                      key={tpl.Id}
                      onClick={() => applyTemplate(tpl)}
                      aria-disabled={!sendable}
                      sx={{
                        p: 1.5,
                        border: '1px solid var(--color-border)',
                        borderRadius: 1,
                        cursor: sendable ? 'pointer' : 'not-allowed',
                        opacity: sendable ? 1 : 0.6,
                        ...(sendable
                          ? { '&:hover': { bgcolor: 'rgba(37, 211, 102, 0.05)', borderColor: '#25D366' } }
                          : {}),
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={600}>{tpl.Title}</Typography>
                        <Chip
                          label={tpl.Type}
                          size="small"
                          sx={{ height: 18, fontSize: '0.6rem', textTransform: 'capitalize' }}
                        />
                        {usingCloud && <MessageTemplateMetaStatus template={tpl} loading={refreshing} />}
                        {usingCloud && templateSpend && (
                          <MessageTemplateSpendChip cost={templateSpend[String(tpl.Id)] ?? 0} />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'var(--color-text-faint)',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {tpl.Body.length > 120 ? `${tpl.Body.slice(0, 120)}...` : tpl.Body}
                      </Typography>
                    </Box>
                  );
                })}

                {templates.length === 0 && !loadingTemplates && (
                  <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', textAlign: 'center', py: 1 }}>
                    No WhatsApp templates found. You can create templates in Campaigns.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 1.5 }}>
              {usingCloud ? (
                <>
                  This approved template will be sent to {client?.Name} as written. Placeholders like{' '}
                  <code>{'{{client_name}}'}</code> are filled in automatically.
                </>
              ) : (
                <>
                  Compose your message to {client?.Name}. Placeholders like{' '}
                  <code>{'{{client_name}}'}</code> and <code>{'{{agent_name}}'}</code> are filled in automatically.
                </>
              )}
            </Typography>
            {usingCloud ? (
              <Box
                sx={{
                  mb: 1,
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid var(--color-border)',
                  bgcolor: 'var(--color-bg)',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: 'var(--color-text-strong)',
                  maxHeight: 220,
                  overflow: 'auto',
                }}
              >
                {message}
              </Box>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`Hi ${client?.Name || ''}! ...`}
                autoFocus
                sx={{ mb: 1 }}
              />
            )}
            <Button
              size="small"
              onClick={() => setStep('pick')}
              disabled={sending}
              sx={{ textTransform: 'none', color: 'var(--color-text-faint)', fontSize: '0.75rem' }}
            >
              ← Back to templates
            </Button>
            {error && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#ef4444' }}>
                {error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={sending}
          sx={{ textTransform: 'none', color: 'var(--color-text-subtle)' }}
        >
          Cancel
        </Button>
        {step === 'edit' && (
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <WhatsAppIcon />}
            onClick={handleSend}
            disabled={sending || !message.trim() || budgetPaused}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, textTransform: 'none' }}
          >
            {sending ? 'Sending...' : 'Send via WhatsApp'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
